import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/prices';

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const CRITERIA_OPTIONS = [
  { value: 1, label: '1 — Grup' },
  { value: 2, label: '2 — Alt Grup' },
  { value: 3, label: '3 — Müşteri Kodu' },
];

const EMPTY_FORM = {
  id: '', productId: '', vendorId: '',
  buyingPrice: '', salePrice: '', returnPrice: '', recommendedPrice: '', vatPercentage: '',
  startDate: '', endDate: '', status: 1,
  description: '', priceComment: '',
  customerId: '', customerCriteriaType: '',
  customerDynamicGroupId: '', customerGroupId: '', customerSubGroupId: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

function toIsoDateInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function rowToForm(r) {
  return {
    id: r.id || r.Id || '',
    productId: r.productId || r.ProductId || '',
    vendorId: r.vendorId || r.VendorId || '',
    buyingPrice: r.buyingPrice ?? r.BuyingPrice ?? '',
    salePrice: r.salePrice ?? r.SalePrice ?? '',
    returnPrice: r.returnPrice ?? r.ReturnPrice ?? '',
    recommendedPrice: r.recommendedPrice ?? r.RecommendedPrice ?? '',
    vatPercentage: r.vatPercentage ?? r.VatPercentage ?? '',
    startDate: toIsoDateInput(r.startDate ?? r.StartDate),
    endDate: toIsoDateInput(r.endDate ?? r.EndDate),
    status: r.status ?? r.Status ?? 1,
    description: r.description ?? r.Description ?? '',
    priceComment: r.priceComment ?? r.PriceComment ?? '',
    customerId: r.customerId ?? r.CustomerId ?? '',
    customerCriteriaType: r.customerCriteriaType ?? r.CustomerCriteriaType ?? '',
    customerDynamicGroupId: r.customerDynamicGroupId ?? r.CustomerDynamicGroupId ?? '',
    customerGroupId: r.customerGroupId ?? r.CustomerGroupId ?? '',
    customerSubGroupId: r.customerSubGroupId ?? r.CustomerSubGroupId ?? '',
  };
}

export function PriceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Fiyat' : 'Fiyat Detayı', 'Ürün satış/alış/iade fiyatı ve müşteri kapsamı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Küçük lookup'lar (vendor/group/subgroup/dynamicgroup) RelationPicker'a rows verir.
  const [vendors, setVendors] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customerDynamicGroups, setCustomerDynamicGroups] = useState([]);

  // Büyük lookup etiketleri (ürün/müşteri) — kaydın id'sine karşılık gelen etiketi
  // lazy çek (4000+/8000+ liste; RelationPicker selectedLabel'i için).
  const [productLabel, setProductLabel] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Küçük lookup'lar başlangıçta yüklenir (vendor/group/subgroup/dynamicgroup küçük listeler).
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendors/Select', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/api/customergroups/Select', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/api/customerdynamicgroups/Select', { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([v, cg, csg, cdg]) => {
      setVendors(unwrapLookupItems(v));
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
      setCustomerDynamicGroups(unwrapLookupItems(cdg));
    }).catch(() => {});
  }, []);

  // Ürün/müşteri etiketlerini id'den lazy çek (RelationPicker selectedLabel).
  const fetchBigLabel = useCallback(async (apiBase, lookupId) => {
    if (!lookupId) return '';
    try {
      const u = `${apiBase}?$filter=${encodeURIComponent(`Id eq ${lookupId}`)}&$top=1&light=1`;
      const r = await fetch(u, { headers: authHeaders() });
      if (!r.ok) return '';
      const rows = getODataRows(await r.json());
      return rows.length ? lookupLabel(rows[0]) : '';
    } catch { return ''; }
  }, []);

  // Form'daki product/customer id değiştikçe etiketini güncelle (load/clone sonrası).
  useEffect(() => {
    let active = true;
    fetchBigLabel('/api/products', form.productId).then((l) => { if (active) setProductLabel(l); });
    return () => { active = false; };
  }, [form.productId, fetchBigLabel]);
  useEffect(() => {
    let active = true;
    fetchBigLabel('/api/customers', form.customerId).then((l) => { if (active) setCustomerLabel(l); });
    return () => { active = false; };
  }, [form.customerId, fetchBigLabel]);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // Listeden gelen satır varsa: anında paint + early return. Ancak liste $select audit
    // alanlarını içermez → audit'i tam kayıttan (GetById) fire-and-forget çekip doldur.
    const row = location.state?.row;
    if (row) {
      setForm(rowToForm(row));
      setLoading(false);
      fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = unwrap(body);
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Fiyat alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Fiyat alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /prices/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm({ ...location.state.clone, id: '' });
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    id: form.id || undefined,
    productId: form.productId,
    vendorId: form.vendorId || null,
    buyingPrice: Number(form.buyingPrice || 0),
    salePrice: Number(form.salePrice || 0),
    returnPrice: Number(form.returnPrice || 0),
    recommendedPrice: form.recommendedPrice === '' ? null : Number(form.recommendedPrice),
    vatPercentage: Number(form.vatPercentage || 0),
    startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
    endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
    status: form.status === '' ? null : Number(form.status),
    description: form.description || null,
    priceComment: form.priceComment || null,
    customerId: form.customerId || null,
    customerCriteriaType: form.customerCriteriaType === '' ? null : Number(form.customerCriteriaType),
    customerDynamicGroupId: form.customerDynamicGroupId || null,
    customerGroupId: form.customerGroupId || null,
    customerSubGroupId: form.customerSubGroupId || null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki fiyat backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.productId) { showToast('error', 'Ürün seçiniz.'); return; }
    if (form.salePrice === '' || Number.isNaN(Number(form.salePrice))) { showToast('error', 'Satış fiyatı zorunlu.'); return; }

    const payload = buildPayload();
    const isEdit = !!form.id;
    if (!isEdit) delete payload.id;

    setSaving(true);
    try {
      const res = await fetch(isEdit ? `${API_BASE}/Update` : `${API_BASE}/Add`, {
        method: isEdit ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body?.message || body?.Message || body?.title || body?.detail
          || (typeof body === 'string' ? body : null)
          || `Kayıt kaydedilemedi (HTTP ${res.status})`;
        throw new Error(msg);
      }
      if (isNew) {
        if (andNew) {
          showToast('success', 'Fiyat eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Fiyat kaydedildi.');
        setTimeout(() => navigate('/prices'), 400);
        return;
      }
      showToast('success', 'Fiyat güncellendi.');
      setTimeout(() => navigate('/prices'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni fiyat oluşmaz.
  const handleClone = () => {
    navigate('/prices/new', { state: { clone: { ...form, id: '' } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: form.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Fiyat pasifleştirildi.');
      setTimeout(() => navigate('/prices'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (() => {
    const meta = STATUS_META[form.status];
    return meta ? <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge> : null;
  })();

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/prices')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni fiyat aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Fiyatlar', to: '/prices' }, { label: isNew ? 'Yeni Fiyat' : 'Fiyat Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Price', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="wallet" title="Fiyat Bilgileri" subtitle="Ürün, fiyatlar, KDV ve geçerlilik tarihleri">
          <Grid cols={3}>
            <FieldCustom label="Ürün" req invalid={tried && !form.productId}>
              <RelationPicker config={RELATION_CONFIGS.product}
                value={form.productId} selectedLabel={productLabel}
                onChange={(id) => set('productId', id)} />
            </FieldCustom>
            <FieldCustom label="Distribütör">
              <RelationPicker config={RELATION_CONFIGS.vendor} rows={vendors}
                value={form.vendorId} selectedLabel={form.vendorId ? lookupLabel(vendors.find(v => (v.id || v.Id) === form.vendorId)) : ''}
                onChange={(val) => set('vendorId', val)} placeholder="(Boş)" />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />

            <FieldText form={form} set={set} k="buyingPrice" label="Alış Fiyatı" num type="number" />
            <FieldText form={form} set={set} tried={tried} k="salePrice" label="Satış Fiyatı" req num type="number" />
            <FieldText form={form} set={set} k="returnPrice" label="İade Fiyatı" num type="number" />

            <FieldText form={form} set={set} k="recommendedPrice" label="Tavsiye Fiyat" num type="number" />
            <FieldText form={form} set={set} k="vatPercentage" label="KDV %" num type="number" suffix="%" />

            <FieldDate form={form} set={set} k="startDate" label="Başlangıç Tarihi" />
            <FieldDate form={form} set={set} k="endDate" label="Bitiş Tarihi" />
          </Grid>
        </Section>

        <Section
          icon="users"
          title="Müşteri Kapsamı"
          subtitle="Fiyatın geçerli olduğu müşteri kapsamı"
          note="Specificity: Müşteri > Alt Grup > Grup > Dinamik Grup > Distribütör > Jenerik"
        >
          <Grid cols={3}>
            <FieldCustom label="Müşteri">
              <RelationPicker config={RELATION_CONFIGS.customer}
                value={form.customerId} selectedLabel={customerLabel}
                onChange={(id) => set('customerId', id)} placeholder="(Tümü)" />
            </FieldCustom>
            <FieldCustom label="Alt Grup">
              <RelationPicker config={RELATION_CONFIGS.customerSubGroup} rows={customerSubGroups}
                value={form.customerSubGroupId} selectedLabel={form.customerSubGroupId ? lookupLabel(customerSubGroups.find(g => (g.id || g.Id) === form.customerSubGroupId)) : ''}
                onChange={(val) => set('customerSubGroupId', val)} placeholder="(Tümü)" />
            </FieldCustom>
            <FieldCustom label="Grup">
              <RelationPicker config={RELATION_CONFIGS.customerGroup} rows={customerGroups}
                value={form.customerGroupId} selectedLabel={form.customerGroupId ? lookupLabel(customerGroups.find(g => (g.id || g.Id) === form.customerGroupId)) : ''}
                onChange={(val) => set('customerGroupId', val)} placeholder="(Tümü)" />
            </FieldCustom>

            <FieldCustom label="Dinamik Grup">
              <RelationPicker config={RELATION_CONFIGS.customerDynamicGroup} rows={customerDynamicGroups}
                value={form.customerDynamicGroupId}
                selectedLabel={form.customerDynamicGroupId ? lookupLabel(customerDynamicGroups.find(g => (g.id || g.Id) === form.customerDynamicGroupId)) : ''}
                onChange={(val) => set('customerDynamicGroupId', val)} placeholder="(Tümü)" />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="customerCriteriaType" label="Kriter Tipi"
              options={[{ value: '', label: '(Yok)' }, ...CRITERIA_OPTIONS]} />
          </Grid>
        </Section>

        <Section icon="info" title="Açıklamalar" subtitle="Yorum ve serbest açıklama">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="priceComment" label="Yorum (max 150)" />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={2} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Fiyatı Pasifleştir"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>Vazgeç</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Pasifleştir'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Bu fiyat tanımını pasifleştirmek istediğinize emin misiniz? (Soft delete — Status 0 olur.)
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default PriceDetailPage;
